import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { getBaseURL } from '../api/client';

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await axios.post(`${getBaseURL()}/auth/forgot-password`, { email });
            setSuccess(true);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error al procesar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-[#0a0f16] flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-[#111827] border border-gray-800 rounded-2xl p-8 text-center">
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 bg-[#00bfa5]/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-10 h-10 text-[#00bfa5]" />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-4">¡Correo enviado!</h2>
                    <p className="text-gray-400 mb-8">
                        Si el correo <span className="text-white font-medium">{email}</span> está registrado, recibirás un enlace para restablecer tu contraseña en unos minutos.
                    </p>
                    <Link
                        to="/login"
                        className="flex items-center justify-center gap-2 text-[#00bfa5] hover:text-[#00d1b2] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Volver al inicio de sesión
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0f16] flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-[#111827] border border-gray-800 rounded-2xl p-8">
                <Link to="/login" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    Volver
                </Link>

                <h2 className="text-3xl font-bold text-white mb-2">Recuperar cuenta</h2>
                <p className="text-gray-400 mb-8">Introduce tu email y te enviaremos instrucciones para restablecer tu contraseña.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-[#1f2937] border border-gray-700 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-[#00bfa5] transition-all"
                                placeholder="tu@email.com"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-500 p-4 rounded-xl text-sm">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#00bfa5] hover:bg-[#00d1b2] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enviar instrucciones'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
